/**
 * Evaluate only once.
 */
export function cache<T>(f: () => T): () => T {
  let result: (() => T) | undefined;
  return () => {
    if (!result) {
      try {
        const r = f();
        result = () => r;
      } catch (e) {
        result = () => {
          throw e;
        };
      }
      f = undefined!; // gc
    }
    return result();
  };
}

/**
 * Cache latest value, based on arguments
 */
export function cacheSingle<F extends (...args: any[]) => any>(f: F): F {
  let args: Parameters<F> | undefined;
  let result: (() => ReturnType<F>) | undefined;
  let this_: ThisType<F> | undefined;
  return <F>function (this: ThisType<F>, ...rest: Parameters<F>) {
    if (
      !result ||
      !Object.is(this, this_) ||
      !args!.every((arg, i) => Object.is(arg, rest[i]))
    ) {
      args = <Parameters<F>>[...rest];
      this_ = this;
      try {
        const r = <ReturnType<F>>f.apply(this, <any>rest);
        result = () => r;
      } catch (e) {
        result = () => {
          throw e;
        };
      }
    }
    return result();
  };
}

/**
 * Self-populating keyed cache
 */
export class KeyedCache<K, T> {
  private readonly cache = new Map<K, T>();

  constructor(private readonly f: (key: K) => T) {}

  delete(key: K) {
    this.cache.delete(key);
  }

  get(key: K): T {
    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }
    const value = this.f(key);
    this.cache.set(key, value);
    return value;
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }
}
